/**
 * Webmail keeps the open folder, message, page and search in the address so a
 * refresh — or a shared link — reopens the same view. They travel as ONE
 * opaque token (`/mail?s=…`) instead of readable `?m=inbox:1443&page=2`.
 *
 * This is obfuscation, not security: the key ships in the bundle, so anyone
 * determined can decode it. Access is still enforced by the BFF on every
 * request. The point is that ids, folder names and search terms are not
 * readable at a glance (screen shares, screenshots, browser history).
 *
 * Format (base64url): [version][salt][checksum][payload XOR keystream(salt)],
 * payload = UTF-8 JSON of the compact state. Old plain links (`?folder&m&page&q`)
 * are still read and rewritten to the token on first load.
 *
 * Keep in sync with EnterpriseMeeting/web/src/features/meeting/mailLink.ts
 * (encoder only), which builds "Open email" links into webmail.
 */
export interface MailUrlState {
    folder?: string;
    m?: string;
    page?: number;
    q?: string;
}

export const MAIL_STATE_PARAM = "s";
const LEGACY_PARAMS = ["folder", "m", "page", "q"] as const;
const VERSION = 1;
const KEY = "fg.mail.url.v1";
const MAX_TEXT = 1024;

function fnv1a(text: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}

const KEY_SEED = fnv1a(KEY);

/** xorshift32 keystream seeded by the key and a per-token salt. */
function keystream(length: number, salt: number): Uint8Array {
    let x = (KEY_SEED ^ Math.imul(salt + 1, 0x9e3779b1)) >>> 0 || 0x2545f491;
    const out = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        x ^= x << 13;
        x >>>= 0;
        x ^= x >>> 17;
        x ^= x << 5;
        x >>>= 0;
        out[i] = x & 0xff;
    }
    return out;
}

function toBase64Url(bytes: Uint8Array): string {
    let binary = "";
    bytes.forEach((b) => {
        binary += String.fromCharCode(b);
    });
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(text: string): Uint8Array | null {
    if (!/^[A-Za-z0-9_-]+$/.test(text)) return null;
    try {
        const b64 = text.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((text.length + 3) % 4);
        const binary = atob(b64);
        const out = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
        return out;
    } catch {
        return null;
    }
}

function checksum(bytes: Uint8Array): number {
    let sum = 0;
    bytes.forEach((b, i) => {
        sum = (sum + b * (i + 1)) & 0xff;
    });
    return sum;
}

/** Drops empty / default fields so equal views produce equal tokens. */
export function compactMailState(state: MailUrlState): MailUrlState {
    const out: MailUrlState = {};
    if (state.folder && state.folder !== "inbox") out.folder = state.folder;
    if (state.m) out.m = state.m;
    if (state.page && Number.isFinite(state.page) && state.page > 1) out.page = Math.floor(state.page);
    if (state.q && state.q.trim()) out.q = state.q.trim();
    return out;
}

export function isEmptyMailState(state: MailUrlState): boolean {
    return Object.keys(compactMailState(state)).length === 0;
}

export function encodeMailState(state: MailUrlState): string {
    const plain = new TextEncoder().encode(JSON.stringify(compactMailState(state)));
    const salt = fnv1a(Array.from(plain, (b) => String.fromCharCode(b)).join("")) & 0xff;
    const stream = keystream(plain.length, salt);
    const out = new Uint8Array(plain.length + 3);
    out[0] = VERSION;
    out[1] = salt;
    out[2] = checksum(plain);
    for (let i = 0; i < plain.length; i++) out[i + 3] = (plain[i] ?? 0) ^ (stream[i] ?? 0);
    return toBase64Url(out);
}

/** The state in a token, or null when it is not one of ours (tampered, truncated, other version). */
export function decodeMailState(token: string | null | undefined): MailUrlState | null {
    if (!token || token.length > 4096) return null;
    const bytes = fromBase64Url(token);
    if (!bytes || bytes.length < 3 || bytes[0] !== VERSION) return null;
    const salt = bytes[1] ?? 0;
    const body = bytes.subarray(3);
    const stream = keystream(body.length, salt);
    const plain = new Uint8Array(body.length);
    for (let i = 0; i < body.length; i++) plain[i] = (body[i] ?? 0) ^ (stream[i] ?? 0);
    if (checksum(plain) !== bytes[2]) return null;
    try {
        const raw: unknown = JSON.parse(new TextDecoder().decode(plain));
        return sanitize(raw);
    } catch {
        return null;
    }
}

function text(value: unknown): string | undefined {
    return typeof value === "string" && value.length > 0 && value.length <= MAX_TEXT ? value : undefined;
}

function sanitize(raw: unknown): MailUrlState | null {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
    const r = raw as Record<string, unknown>;
    const page = typeof r.page === "number" && Number.isInteger(r.page) && r.page > 1 && r.page <= 100_000 ? r.page : undefined;
    return compactMailState({ folder: text(r.folder), m: text(r.m), page, q: text(r.q) });
}

/** The view in the address: the `s` token, else the old plain parameters. */
export function readMailState(params: URLSearchParams): MailUrlState {
    const token = params.get(MAIL_STATE_PARAM);
    if (token !== null) return decodeMailState(token) ?? {};
    const page = Number(params.get("page"));
    return compactMailState({
        folder: params.get("folder") ?? undefined,
        m: params.get("m") ?? undefined,
        page: Number.isFinite(page) ? page : undefined,
        q: params.get("q") ?? undefined
    });
}

/** `prev` with the view written as a token (plain parameters removed; unrelated ones kept). */
export function writeMailState(prev: URLSearchParams, state: MailUrlState): URLSearchParams {
    const next = new URLSearchParams(prev);
    LEGACY_PARAMS.forEach((key) => next.delete(key));
    if (isEmptyMailState(state)) next.delete(MAIL_STATE_PARAM);
    else next.set(MAIL_STATE_PARAM, encodeMailState(state));
    return next;
}

/** An in-app link to a webmail view. */
export function mailHref(state: MailUrlState): string {
    return isEmptyMailState(state) ? "/mail" : `/mail?${MAIL_STATE_PARAM}=${encodeMailState(state)}`;
}
